import { css } from '@emotion/react';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import CommunicationEmailPreview from 'communications/course_communications/components/communication-item/communication-email-preview';
import SamplePushNotification from 'communications/course_communications/components/communication-modal/sample_push_notification/sample-push-notification';
import { getCurrentCourse } from 'redux/selectors/course';
import { CommunicationType } from 'redux/schemas/models/course-communication';
import { gray4, primary } from 'styles/global_defaults/colors';
import { semiBoldFontWeight } from 'styles/global_defaults/fonts';
import { sanitize, SanitizationLevel } from 'froala/helpers/sanitizer';
import { ContactLearnersFormValues } from './contact-learners-form';

const styles = css`
  .email-section-header {
    border-top: 2px solid ${gray4};
    border-bottom: 1px solid ${gray4};
  }
`;

const sendTestEmailStyles = (isEnabled: boolean) => css`
  color: ${isEnabled ? primary : gray4};
  cursor: ${isEnabled ? 'pointer' : 'auto'};
  font-weight: ${semiBoldFontWeight};
`;

type StepPreviewProps = {
  onSendTestEmail: (
    communication: { subject: string; body: string },
    push?: {
      enablePushNotification?: boolean;
      pushNotificationBody?: string;
    },
  ) => Promise<unknown>;
};

/**
 * Step 2 body — read-only preview of the composed message.
 * Reads values from the shared RHF FormProvider via useWatch.
 */
const StepPreview = ({ onSendTestEmail }: StepPreviewProps) => {
  const { control } = useFormContext<ContactLearnersFormValues>();
  const currentCourse = useSelector(getCurrentCourse);
  const [isTestEmailSending, setIsTestEmailSending] = useState(false);
  const [subject, body, includePushNotification, pushNotificationBody] = useWatch({
    control,
    name: ['subject', 'body', 'includePushNotification', 'pushNotificationBody'],
  });

  const isSendTestEmailEnabled = !!(subject && body) && !isTestEmailSending;
  const sendTestEmailText = isTestEmailSending ? t.COURSE_COMMUNICATIONS.SENDING() : t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.SEND_TEST_EMAIL();

  const handleSendTestEmail = async () => {
    if (!isSendTestEmailEnabled) return;
    setIsTestEmailSending(true);
    await onSendTestEmail(
      { subject, body },
      includePushNotification ? { enablePushNotification: true, pushNotificationBody } : undefined,
    ).catch(() => {});
    setIsTestEmailSending(false);
  };

  return (
    <div css={styles}>
      <div className='email-section-header d-flex align-items-center justify-content-between font-weight-bolder py-4 mt-4 mb-4'>
        <span className='font-size-large font-weight-bolder'>{t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.EMAIL_SECTION()}</span>
        <span
          className='text-small'
          css={sendTestEmailStyles(isSendTestEmailEnabled)}
          onClick={handleSendTestEmail}
        >
          {sendTestEmailText}
        </span>
      </div>
      <CommunicationEmailPreview
        subject={subject}
        body={body}
        communicationType={CommunicationType.LEARNER_PROGRESS_EMAIL}
      />
      {includePushNotification && (
        <div>
          <SamplePushNotification
            notification={{
              title: sanitize(currentCourse?.name ?? '', SanitizationLevel.NO_TAGS),
              bodyText: sanitize(pushNotificationBody, SanitizationLevel.NO_TAGS),
            }}
          />
        </div>
      )}
    </div>
  );
};

export default StepPreview;
